"use client";

import { Task } from "@/app/queries";
import { type DBTask } from "@/db/schemas/tasks";
import { GRID_COLORS } from "@/lib/arc";
import { useEffect, useRef } from "react";
type ColorIndex = (typeof GRID_COLORS)[number][1];
type Grid = ColorIndex[][];

interface GridThumbnailProps {
  grid: Grid;
  width?: number;
  height?: number;
  maxWidth?: number;
  maxHeight?: number;
  cellSize?: number;
  className?: string;
  border?: boolean;
  containerSize?: number;
}

export function GridThumbnail({
  grid,
  width = 200,
  height = 200,
  maxWidth,
  maxHeight,
  cellSize,
  className = "",
  border = true,
  containerSize = 50, // Default container size of 50px × 50px
}: GridThumbnailProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate dimensions based on grid and constraints
  const rows = grid.length;
  const cols = rows > 0 ? grid[0].length : 0;

  // Calculate dimensions to maintain square cells
  let finalWidth = width;
  let finalHeight = height;

  if (cellSize) {
    // If cellSize is provided, use it to calculate dimensions
    finalWidth = cols * cellSize;
    finalHeight = rows * cellSize;
  } else {
    // Calculate the aspect ratio of the grid
    const gridAspectRatio = cols / rows;

    if (containerSize) {
      // Fit within the container size while maintaining aspect ratio
      if (gridAspectRatio >= 1) {
        // Wider grid: constrain by width
        finalWidth = containerSize;
        finalHeight = containerSize / gridAspectRatio;
      } else {
        // Taller grid: constrain by height
        finalHeight = containerSize;
        finalWidth = containerSize * gridAspectRatio;
      }
    }

    // Apply additional constraints if provided
    if (maxWidth && finalWidth > maxWidth) {
      finalWidth = maxWidth;
      finalHeight = finalWidth / gridAspectRatio;
    }

    if (maxHeight && finalHeight > maxHeight) {
      finalHeight = maxHeight;
      finalWidth = finalHeight * gridAspectRatio;
    }
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Get device pixel ratio
    const dpr = window.devicePixelRatio || 1;

    // Get the actual container size if it exists
    const container = containerRef.current;
    let containerWidth = finalWidth;
    let containerHeight = finalHeight;

    if (container && containerSize) {
      containerWidth = containerSize;
      containerHeight = containerSize;
    }

    // Set canvas dimensions accounting for device pixel ratio
    // Use the container dimensions directly to fill the entire space
    const displayWidth = containerWidth;
    const displayHeight = containerHeight;

    // Set the "actual" size of the canvas
    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr;

    // Scale the context to ensure correct drawing operations
    ctx.scale(dpr, dpr);

    // Set CSS size to fill container completely
    canvas.style.width = `${displayWidth}px`;
    canvas.style.height = `${displayHeight}px`;

    // Clear canvas
    ctx.clearRect(0, 0, displayWidth, displayHeight);

    // Calculate grid dimensions
    const rows = grid.length;
    const cols = rows > 0 ? grid[0].length : 0;

    if (rows === 0 || cols === 0) return;

    // Calculate cell dimensions to fill the entire canvas
    // while maintaining square cells
    const gridAspectRatio = cols / rows;
    let cellWidth, cellHeight;

    if (gridAspectRatio >= 1) {
      // Wider grid: cells are determined by width
      cellWidth = displayWidth / cols;
      cellHeight = cellWidth;
    } else {
      // Taller grid: cells are determined by height
      cellHeight = displayHeight / rows;
      cellWidth = cellHeight;
    }

    // Calculate offset to center the grid
    const offsetX = (displayWidth - cellWidth * cols) / 2;
    const offsetY = (displayHeight - cellHeight * rows) / 2;

    // Draw grid
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const colorIndex = grid[y][x];
        const colorHex =
          GRID_COLORS.find((c) => c[1] === colorIndex)?.[2] || "#FFFFFF";

        ctx.fillStyle = colorHex;
        ctx.fillRect(
          offsetX + x * cellWidth,
          offsetY + y * cellHeight,
          cellWidth,
          cellHeight,
        );

        // Add cell border if needed
        if (border) {
          ctx.strokeStyle = "rgba(0, 0, 0, 0.1)";
          ctx.lineWidth = 1;
          ctx.strokeRect(
            offsetX + x * cellWidth,
            offsetY + y * cellHeight,
            cellWidth,
            cellHeight,
          );
        }
      }
    }
  }, [grid, finalWidth, finalHeight, cellSize, border, containerSize]);

  return (
    <div
      ref={containerRef}
      style={{
        width: containerSize ? `${containerSize}px` : "auto",
        height: containerSize ? `${containerSize}px` : "auto",
        display: "block", // Changed from flex to block
        position: "relative",
        overflow: "hidden", // Ensure nothing spills outside
      }}
      className={className}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: "block", // Ensures no extra space
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
        className={border ? "border border-border" : ""}
      />
    </div>
  );
}

export function TaskThumbnail({ task }: { task: NonNullable<Task> }) {
  const size = 30;
  return (
    <div className="flex flex-row gap-[4px]">
      {task.train.map((train, i) => (
        <div className="flex flex-col gap-[2px]" key={task.id + "-train-" + i}>
          <GridThumbnail grid={train.input as Grid} containerSize={size} />
          <GridThumbnail grid={train.output as Grid} containerSize={size} />
        </div>
      ))}
      {task.test.map((test, i) => (
        <GridThumbnail
          key={task.id + "-test-" + i}
          grid={test.input as Grid}
          containerSize={size}
        />
      ))}
    </div>
  );
}
